import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BannedEntitySummary,
  BannedPost,
  BannedUserSummary,
  ModerationDecision,
} from 'services/api/banInfo/types';
import { Avatar } from 'ui/Avatar/Avatar';
import { BlockedEntity, BlockedEntityDetails } from '../model/types';
import {
  formatDateTime,
  getManagementPath,
  initials,
  valueFromRecord,
} from './helpers';

interface Props {
  entity: BlockedEntity | null;
  details: BlockedEntityDetails | null;
  loading: boolean;
  error: string;
  pageSize: number;
  logsPage: number;
  postsPage: number;
  logsLoading: boolean;
  postsLoading: boolean;
  onLogsPageChange: (page: number) => void;
  onPostsPageChange: (page: number) => void;
  onInspectLog: (item: ModerationDecision) => void;
  onInspectPost: (item: BannedPost) => void;
}

type DetailsTab = 'overview' | 'history' | 'content';

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => {
  const isMissing = value === undefined || value === null || value === '';
  return (
    <div className="blocked-details__row">
      <span>{label}</span>
      <strong>{isMissing ? 'Not provided' : value}</strong>
    </div>
  );
};

const getVisiblePages = (page: number, pages: number): number[] => {
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const end = Math.min(pages, Math.max(page + 2, 5));
  const result: number[] = [];
  for (let value = start; value <= end; value += 1) result.push(value);
  return result;
};

const isFalseFlag = (value: unknown): boolean =>
  value === false || value === 0 || value === '0' || value === 'false';

const booleanLabel = (value: unknown): string =>
  isFalseFlag(value) ? 'No' : 'Yes';

interface ActivityPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  loading: boolean;
  label: string;
  onPageChange: (page: number) => void;
}

const ActivityPagination: React.FC<ActivityPaginationProps> = ({
  page,
  pageSize,
  total,
  loading,
  label,
  onPageChange,
}) => {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);

  return (
    <footer className="blocked-details__activity-footer">
      <span>
        Showing {start}–{end} of {total.toLocaleString()}
      </span>
      <nav
        className="blocked-pagination blocked-details__activity-pagination"
        aria-label={label}
      >
        <button
          type="button"
          disabled={page <= 1 || loading}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        {getVisiblePages(page, pages).map((value) => (
          <button
            key={value}
            type="button"
            disabled={loading}
            className={
              value === page ? 'blocked-pagination__active' : undefined
            }
            onClick={() => onPageChange(value)}
            aria-current={value === page ? 'page' : undefined}
          >
            {value}
          </button>
        ))}
        <button
          type="button"
          disabled={page >= pages || loading}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          ›
        </button>
      </nav>
    </footer>
  );
};

export const BlockedDetailsPanel: React.FC<Props> = ({
  entity,
  details,
  loading,
  error,
  pageSize,
  logsPage,
  postsPage,
  logsLoading,
  postsLoading,
  onLogsPageChange,
  onPostsPageChange,
  onInspectLog,
  onInspectPost,
}) => {
  const [tab, setTab] = useState<DetailsTab>('overview');
  const summary = details?.summary || null;
  const summaryRecord = (summary || {}) as Record<string, unknown>;
  const userSummary =
    entity?.kind === 'users' ? (summary as BannedUserSummary | null) : null;
  const user = userSummary?.user || null;
  const peerSummary =
    entity?.kind === 'users' ? null : (summary as BannedEntitySummary | null);

  useEffect(() => {
    setTab('overview');
  }, [entity?.id, entity?.kind]);

  const categoryCounts = useMemo(
    () =>
      Array.isArray(summary?.counts_by_category)
        ? summary!.counts_by_category!
        : [],
    [summary],
  );

  if (!entity) {
    return (
      <aside className="blocked-details blocked-details--empty">
        <span className="blocked-details__empty-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3 4 6v5c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V6l-8-3Zm0 5v5m0 3h.01" />
          </svg>
        </span>
        <strong>Select an entity</strong>
        <p>
          Choose a row to inspect all available Auto-Moderation information.
        </p>
      </aside>
    );
  }

  const banRecord = (userSummary?.ban || {}) as Record<string, unknown>;
  const registrationDate =
    valueFromRecord(user, ['registration_date', 'register_at', 'created_at']) ||
    valueFromRecord(summaryRecord, ['created_at']) ||
    entity.createdAt;
  const lastActivity = valueFromRecord(user, [
    'last_activity',
    'last_active',
    'last_login',
    'updated_at',
  ]);
  const phone =
    valueFromRecord(user, ['phone']) ||
    valueFromRecord(summaryRecord, ['phone']);
  const rawAvatar =
    valueFromRecord(user, ['photo_url', 'avatar_url', 'avatar', 'photo']) ||
    valueFromRecord(summaryRecord, [
      'photo_url',
      'avatar_url',
      'avatar',
      'photo',
    ]) ||
    entity.avatarUrl ||
    '';
  const avatarUrl = /^(https?:|blob:|data:)/i.test(rawAvatar) ? rawAvatar : '';
  const avatarHash =
    valueFromRecord(user, ['photo_access_hash']) ||
    valueFromRecord(summaryRecord, ['photo_access_hash']) ||
    (!avatarUrl && /^\d+$/.test(rawAvatar) ? rawAvatar : '') ||
    entity.avatarHash ||
    '';
  const blockingReason =
    valueFromRecord(summaryRecord, [
      'blocking_reason',
      'restriction_reason',
      'delete_reason',
      'reason_text',
    ]) ||
    valueFromRecord(banRecord, ['restriction_reason']) ||
    entity.reason;
  const blockedAt =
    valueFromRecord(summaryRecord, ['banned_at', 'blocked_at']) ||
    valueFromRecord(banRecord, ['banned_at', 'blocked_at']) ||
    entity.blockedAt;
  const currentStatus =
    isFalseFlag(summaryRecord.deactivated) ||
    isFalseFlag(summaryRecord.restricted) ||
    isFalseFlag(summaryRecord.blocked) ||
    isFalseFlag(banRecord.restricted)
      ? 'Review required'
      : entity.status;
  const confirmedViolations =
    summary?.confirmed_violations ?? entity.confirmedViolations;
  const ownerId =
    valueFromRecord(summaryRecord, [
      'creator_user_id',
      'owner_id',
      'user_id',
    ]) || entity.ownerId;
  const owner =
    valueFromRecord(summaryRecord, [
      'creator_username',
      'owner_username',
      'owner',
    ]) ||
    entity.owner ||
    (ownerId ? `User #${ownerId}` : '');
  const participantCount = summary?.participant_count ?? entity.count;
  const entityCreatedAt = summary?.created_at || entity.createdAt;
  const entityUsername = summary?.username || entity.username;
  const entityName = summary?.title || entity.name;
  const lastDecision = summary?.last_ban_decision || null;

  return (
    <aside className="blocked-details">
      <div className="blocked-details__profile">
        <div className="blocked-details__avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" />
          ) : avatarHash ? (
            <Avatar hash={avatarHash} />
          ) : (
            initials(entity.name)
          )}
        </div>
        <div className="blocked-details__profile-main">
          <span className="blocked-details__eyebrow">
            {entity.kind === 'users'
              ? 'Blocked user'
              : entity.kind === 'groups'
                ? 'Blocked group'
                : 'Blocked channel'}
          </span>
          <h2>{entityName}</h2>
          <p>{entityUsername || `Internal ID #${entity.id}`}</p>
        </div>
        <span
          className={`blocked-status blocked-status--${
            currentStatus === 'Blocked' ? 'blocked' : 'review'
          }`}
        >
          <i />
          {currentStatus}
        </span>
      </div>

      <nav className="blocked-details__tabs" aria-label="Entity details">
        <button
          type="button"
          className={
            tab === 'overview' ? 'blocked-details__tab--active' : undefined
          }
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        {entity.kind === 'users' ? (
          <>
            <button
              type="button"
              className={
                tab === 'history' ? 'blocked-details__tab--active' : undefined
              }
              onClick={() => setTab('history')}
            >
              Decisions
              {details?.logsTotal ? <span>{details.logsTotal}</span> : null}
            </button>
            <button
              type="button"
              className={
                tab === 'content' ? 'blocked-details__tab--active' : undefined
              }
              onClick={() => setTab('content')}
            >
              Content
              {details?.postsTotal ? <span>{details.postsTotal}</span> : null}
            </button>
          </>
        ) : null}
      </nav>

      <div className="blocked-details__body">
        {loading ? (
          <div className="blocked-details__loading">
            <span />
            <span />
            <span />
            <span />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="blocked-details__error">
            <strong>Some details are unavailable</strong>
            <span>{error}</span>
          </div>
        ) : null}

        {!loading && tab === 'overview' ? (
          <>
            <section className="blocked-details__section">
              <h3>Blocking information</h3>
              <InfoRow label="Blocking reason" value={blockingReason} />
              <InfoRow
                label="Date blocked"
                value={formatDateTime(String(blockedAt || ''))}
              />
              <InfoRow label="Current status" value={currentStatus} />
              <InfoRow label="Internal ID" value={`#${entity.id}`} />
              {confirmedViolations != null ? (
                <InfoRow
                  label="Confirmed violations"
                  value={confirmedViolations}
                />
              ) : null}
            </section>

            {entity.kind === 'users' ? (
              <section className="blocked-details__section">
                <h3>User information</h3>
                <InfoRow label="Name" value={entity.name} />
                <InfoRow label="Username" value={entityUsername} />
                <InfoRow label="Phone" value={phone} />
                <InfoRow
                  label="Registration date"
                  value={formatDateTime(String(registrationDate || ''))}
                />
                <InfoRow
                  label="Last activity"
                  value={formatDateTime(lastActivity)}
                />
                {userSummary?.ban?.restricted != null ? (
                  <InfoRow
                    label="Restricted"
                    value={booleanLabel(userSummary.ban.restricted)}
                  />
                ) : null}
              </section>
            ) : (
              <section className="blocked-details__section">
                <h3>
                  {entity.kind === 'groups'
                    ? 'Group information'
                    : 'Channel information'}
                </h3>
                <InfoRow label="Title" value={entityName} />
                <InfoRow label="Username" value={entityUsername} />
                <InfoRow label="About" value={peerSummary?.about} />
                <InfoRow label="Owner" value={owner} />
                <InfoRow
                  label="Owner ID"
                  value={ownerId ? `#${ownerId}` : ''}
                />
                <InfoRow
                  label="Creation date"
                  value={formatDateTime(String(entityCreatedAt || ''))}
                />
                <InfoRow
                  label={entity.kind === 'groups' ? 'Members' : 'Subscribers'}
                  value={participantCount?.toLocaleString()}
                />
                <InfoRow label="Category" value={peerSummary?.category} />
                <InfoRow label="Country" value={peerSummary?.country} />
                {peerSummary?.deactivated != null ? (
                  <InfoRow
                    label="Deactivated"
                    value={booleanLabel(peerSummary.deactivated)}
                  />
                ) : null}
                {peerSummary?.deleted != null ? (
                  <InfoRow
                    label="Deleted"
                    value={booleanLabel(peerSummary.deleted)}
                  />
                ) : null}
              </section>
            )}

            {categoryCounts.length ? (
              <section className="blocked-details__section">
                <h3>Violations by category</h3>
                <div className="blocked-details__chips">
                  {categoryCounts.map((item, index) => (
                    <span key={`${item.category || 'category'}-${index}`}>
                      {item.category || `Category ${item.value ?? index + 1}`}
                      <b>{item.count ?? 0}</b>
                    </span>
                  ))}
                </div>
              </section>
            ) : null}

            {lastDecision ? (
              <section className="blocked-details__section">
                <h3>Latest moderation decision</h3>
                <button
                  type="button"
                  className="blocked-details__decision"
                  onClick={() => onInspectLog(lastDecision)}
                >
                  <span>
                    <strong>
                      {lastDecision.category ||
                        lastDecision.judge_verdict ||
                        `Decision #${lastDecision.id}`}
                    </strong>
                    <small>
                      {lastDecision.reason_text || 'Open full decision'}
                    </small>
                  </span>
                  <b>View</b>
                </button>
              </section>
            ) : null}
          </>
        ) : null}

        {!loading && tab === 'history' && entity.kind === 'users' ? (
          <section className="blocked-details__section blocked-details__section--activity">
            <div className="blocked-details__section-heading">
              <h3>Moderation decisions</h3>
              <span>{(details?.logsTotal || 0).toLocaleString()} total</span>
            </div>
            {logsLoading ? (
              <div className="blocked-details__activity-loading" role="status">
                Loading decisions…
              </div>
            ) : null}
            {details?.logs.length ? (
              <div
                className={
                  logsLoading
                    ? 'blocked-activity-list blocked-activity-list--loading'
                    : 'blocked-activity-list'
                }
              >
                {details.logs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onInspectLog(item)}
                  >
                    <span className="blocked-activity-list__icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 3 4 6v5c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V6l-8-3Zm-3 9 2 2 4-4" />
                      </svg>
                    </span>
                    <span className="blocked-activity-list__main">
                      <strong>
                        {item.category ||
                          item.judge_verdict ||
                          `Decision #${item.id}`}
                      </strong>
                      <small>
                        {item.reason_text ||
                          item.action_tier ||
                          'No reason text'}
                      </small>
                    </span>
                    <time>{formatDateTime(item.created_at)}</time>
                  </button>
                ))}
              </div>
            ) : (
              <div className="blocked-details__empty-list">
                No moderation decisions returned.
              </div>
            )}
            <ActivityPagination
              page={logsPage}
              pageSize={pageSize}
              total={details?.logsTotal || 0}
              loading={logsLoading}
              label="Moderation decisions pagination"
              onPageChange={onLogsPageChange}
            />
          </section>
        ) : null}

        {!loading && tab === 'content' && entity.kind === 'users' ? (
          <section className="blocked-details__section blocked-details__section--activity">
            <div className="blocked-details__section-heading">
              <h3>Posts and messages</h3>
              <span>{(details?.postsTotal || 0).toLocaleString()} total</span>
            </div>
            {postsLoading ? (
              <div className="blocked-details__activity-loading" role="status">
                Loading content…
              </div>
            ) : null}
            {details?.posts.length ? (
              <div
                className={
                  postsLoading
                    ? 'blocked-activity-list blocked-activity-list--loading'
                    : 'blocked-activity-list'
                }
              >
                {details.posts.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onInspectPost(item)}
                  >
                    <span className="blocked-activity-list__icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M5 4h14v12H8l-3 3V4Zm3 4h8m-8 4h5" />
                      </svg>
                    </span>
                    <span className="blocked-activity-list__main">
                      <strong>
                        {item.restriction_category ||
                          item.judge_category_name ||
                          `Content #${item.id}`}
                      </strong>
                      <small>
                        {item.reason_text ||
                          item.judge_verdict ||
                          'Open blocked content'}
                      </small>
                    </span>
                    <time>{formatDateTime(item.created_at)}</time>
                  </button>
                ))}
              </div>
            ) : (
              <div className="blocked-details__empty-list">
                No blocked posts or messages returned.
              </div>
            )}
            <ActivityPagination
              page={postsPage}
              pageSize={pageSize}
              total={details?.postsTotal || 0}
              loading={postsLoading}
              label="Blocked content pagination"
              onPageChange={onPostsPageChange}
            />
          </section>
        ) : null}
      </div>

      <Link
        className="blocked-details__management"
        to={getManagementPath(entity)}
      >
        Open management page
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14 5h5v5M10 14 19 5M19 14v5H5V5h5" />
        </svg>
      </Link>
    </aside>
  );
};
